import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  FLIPKART_END_DATE_TERMINAL_STATUSES,
  parseFlipkartCampaignEndDateJob,
  parseFlipkartCampaignEndDateResult,
  type FlipkartCampaignEndDateJob,
  type FlipkartCampaignEndDateResult,
} from '@/api/flipkartCampaignEndDateJob';
import { flipkartAdsApi } from '@/api/client';

const POLL_MS = 2000;

function markPollFailed(
  setJob: (updater: (current: FlipkartCampaignEndDateJob | null) => FlipkartCampaignEndDateJob | null) => void,
  error: unknown
) {
  const message = error instanceof Error && error.message ? error.message : 'End date job failed';
  setJob((current) =>
    current
      ? {
          ...current,
          status: 'failed',
          applyProgress: { ...current.applyProgress, error: message },
        }
      : current
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollJobUntilDone(
  credentialId: string,
  jobId: string,
  onUpdate: (job: FlipkartCampaignEndDateJob) => void,
  abortRef: { current: boolean }
): Promise<FlipkartCampaignEndDateJob> {
  for (;;) {
    const res = await flipkartAdsApi.getCampaignEndDateJob(credentialId, jobId);
    const job = parseFlipkartCampaignEndDateJob(res.data);
    if (!job) throw new Error('Invalid end date job response');
    onUpdate(job);
    if (FLIPKART_END_DATE_TERMINAL_STATUSES.has(job.status)) return job;
    if (abortRef.current) return job;
    await sleep(POLL_MS);
  }
}

export function useFlipkartCampaignEndDateJob(credentialId: string, adAccountId: string) {
  const [job, setJob] = useState<FlipkartCampaignEndDateJob | null>(null);
  const [failedResults, setFailedResults] = useState<FlipkartCampaignEndDateResult[]>([]);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    setJob(null);
    setFailedResults([]);
    return () => {
      abortRef.current = true;
    };
  }, [adAccountId, credentialId]);

  const loadFailedResults = useCallback(
    async (jobId: string) => {
      const res = await flipkartAdsApi.listCampaignEndDateJobResults(credentialId, jobId, {
        status: 'failed',
        limit: 100,
        offset: 0,
      });
      const raw = (res.data as { results?: unknown[] } | null)?.results ?? [];
      setFailedResults(
        raw
          .map((item) => parseFlipkartCampaignEndDateResult(item))
          .filter((item): item is FlipkartCampaignEndDateResult => item != null)
      );
    },
    [credentialId]
  );

  const startMutation = useMutation({
    mutationFn: async (input: {
      adAccountId: string;
      campaignIds: string[];
      endDate: string | null;
    }) => {
      const createRes = await flipkartAdsApi.createCampaignEndDateJob(credentialId, {
        ad_account_id: input.adAccountId,
        campaign_ids: input.campaignIds,
        end_date: input.endDate,
      });
      const created = parseFlipkartCampaignEndDateJob(createRes.data);
      if (!created) throw new Error('Failed to start end date job');
      setJob(created);
      setFailedResults([]);
      if (FLIPKART_END_DATE_TERMINAL_STATUSES.has(created.status)) {
        if ((created.totals.failed ?? 0) > 0) await loadFailedResults(created.id);
        return created;
      }
      try {
        const finalJob = await pollJobUntilDone(credentialId, created.id, setJob, abortRef);
        if ((finalJob.totals.failed ?? 0) > 0) await loadFailedResults(finalJob.id);
        return finalJob;
      } catch (error) {
        markPollFailed(setJob, error);
        throw error;
      }
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const retryRes = await flipkartAdsApi.retryCampaignEndDateJob(credentialId, jobId);
      const restarted = parseFlipkartCampaignEndDateJob(retryRes.data);
      if (!restarted) throw new Error('Failed to retry end date job');
      setJob(restarted);
      setFailedResults([]);
      try {
        const finalJob = await pollJobUntilDone(credentialId, restarted.id, setJob, abortRef);
        if ((finalJob.totals.failed ?? 0) > 0) await loadFailedResults(finalJob.id);
        return finalJob;
      } catch (error) {
        markPollFailed(setJob, error);
        throw error;
      }
    },
  });

  const isRunning =
    startMutation.isPending ||
    retryMutation.isPending ||
    (job != null && !FLIPKART_END_DATE_TERMINAL_STATUSES.has(job.status));

  return {
    job,
    failedResults,
    isRunning,
    start: startMutation.mutateAsync,
    retryFailed: retryMutation.mutateAsync,
    reset: () => {
      setJob(null);
      setFailedResults([]);
      startMutation.reset();
      retryMutation.reset();
    },
    error: startMutation.error ?? retryMutation.error,
  };
}
